import React, { useState } from 'react'
import { Button, notification } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { buildAllergenPdf } from '../../../../utils/pdf/buildAllergenPdf'

// Converts grid data to PDF format, preserving section information
function adaptGridRowsToPdf(gridRows) {
  return (gridRows || [])
    .filter(r => r.type !== 'section') // Filter out section header items
    .map(r => {
      // Pick a sensible title field
      const name =
        r.name || r.dishName || r.recipeName || r.title || r.recipeTitle || ''

      // Preserve section information for PDF grouping
      const section = r.section || r.sectionName || null

      // Return the dish object with all necessary fields for PDF generation
      return {
        name,
        title: name, // For compatibility
        recipeTitle: name, // For compatibility
        dishName: name, // For compatibility
        section,
        sectionName: section, // For compatibility
        allergens: r.allergens || [],
        allergenTraces: r.allergenTraces || [],
        venueProductsAllergens: r.venueProductsAllergens || null,
        allergenAssignments: r.allergenAssignments || [],
      }
    })
}

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

export const AllergenTableDownloadButton = ({
  isAllRecipes = false,
  allergenData = null, // Pass the allergen data directly
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleDownload = async () => {
    try {
      setIsLoading(true)

      if (!allergenData) {
        throw new Error('No allergen data available for PDF generation')
      }

      // Standard allergen order
      const allergenOrder = [
        'celery',
        'gluten',
        'crustaceans',
        'eggs',
        'fish',
        'lupin',
        'milk',
        'molluscs',
        'mustard',
        'nuts',
        'peanuts',
        'sesame_seeds',
        'soya',
        'sulphites',
      ]

      // Use the robust adapter to convert grid data to PDF format
      const adaptedDishes = adaptGridRowsToPdf(allergenData.dishes || [])

      // Extract menu name from title if it exists (format: "Venue – Menu – Allergen Information")
      const titleParts = allergenData.title?.split(' – ') || []
      const menuName = titleParts.length >= 3 ? titleParts[1] : null

      // Debug: Log the allergen data to see what's available
      // console.log('PDF Generation - allergenData:', allergenData)
      // console.log('PDF Generation - businessName:', allergenData.businessName)
      // console.log('PDF Generation - venueName:', allergenData.venueName)

      // Generate PDF using the builder
      const doc = buildAllergenPdf({
        venueName:
          allergenData.businessName || allergenData.venueName || 'Restaurant',
        menuName: menuName,
        dishes: adaptedDishes,
        allergenOrder,
      })

      // Save the PDF
      const filename = isAllRecipes
        ? 'all-recipes-allergens.pdf'
        : `${
            allergenData.businessName || allergenData.venueName || 'Restaurant'
          }-allergens.pdf`

      doc.save(filename)
    } catch (error) {
      console.error('PDF generation error:', error)
      notification.error({
        message: 'Allergen PDF download failed',
        description:
          'We could not generate the allergen table. Please try again, and contact support if it keeps happening.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Button
      type='primary'
      onClick={handleDownload}
      disabled={isLoading || !allergenData}
      style={{ display: 'flex', alignItems: 'center' }}
    >
      {isLoading ? <LoadingSpinner /> : <DownloadOutlined />}
      Download Allergens Table as PDF
    </Button>
  )
}
